import {inject} from '@angular/core';
import {CanActivateFn, Router, UrlTree} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {isNativeCapacitorPlatform} from '../shared/platform';
import {AuthError, AuthService} from './auth.service';
import type {CurrentUserResponse} from './auth.models';
import {AppLanguageService} from '../shared/i18n/app-language.service';
import {
  buildLocalizedRoutePathname,
  extractLocalizedRouteLanguageFromPathname,
  stripLocalizedRouteLanguageFromPathname,
  type AppLanguageCode,
} from '../shared/i18n/app-language';

function buildLocalizedRedirectPath(currentUrl: string, targetPathname: string): string {
  if (typeof window === 'undefined') {
    return targetPathname;
  }

  const currentPathname = new URL(currentUrl, window.location.origin).pathname;
  const currentLanguage = extractLocalizedRouteLanguageFromPathname(currentPathname);
  if (!currentLanguage) {
    return targetPathname;
  }

  return buildLocalizedRoutePathname(targetPathname, currentLanguage as AppLanguageCode);
}

function readQrTokenFromUrl(currentUrl: string): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return new URL(currentUrl, window.location.origin).searchParams.get('qr')?.trim() ?? '';
  } catch {
    return '';
  }
}

function isNativeGoogleAuthLoginUrl(currentUrl: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const url = new URL(currentUrl, window.location.origin);
    return stripLocalizedRouteLanguageFromPathname(url.pathname) === '/login'
      && url.searchParams.get('nativeGoogleAuth') === '1';
  } catch {
    return false;
  }
}

function shouldAllowBackendRecovery(error: unknown): boolean {
  return error instanceof AuthError && (error.status === 0 || error.status >= 500);
}

export const authGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUrl = state.url || router.url;
  const appLanguageService = inject(AppLanguageService);

  if (!await authService.ensureActiveSession()) {
    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {
      queryParams: {
        returnUrl: currentUrl.trim(),
      },
    });
  }

  try {
    const currentUser = await firstValueFrom(authService.getCurrentUser());
    if (!isNativeCapacitorPlatform()) {
      // Re-entering the web shell can reuse the session after a temporary public locale.
      await appLanguageService.useProfileLanguage(currentUser.preferredLanguageCode).catch(() => undefined);
    }
    return true;
  } catch (error) {
    if (error instanceof AuthError && error.status === 401 && error.code === 'InvalidSession') {
      return false;
    }

    if (shouldAllowBackendRecovery(error)) {
      return true;
    }

    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {
      queryParams: {
        returnUrl: currentUrl.trim(),
      },
    });
  }
};

export const adminGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUrl = state.url || router.url;

  if (!await authService.ensureActiveSession()) {
    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {
      queryParams: {
        returnUrl: currentUrl.trim(),
      },
    });
  }

  try {
    const currentUser = await firstValueFrom(authService.getCurrentUser());
    if (isAdministrator(currentUser)) {
      return true;
    }

    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/home')]);
  } catch (error) {
    if (error instanceof AuthError && error.status === 401 && error.code === 'InvalidSession') {
      return false;
    }

    if (shouldAllowBackendRecovery(error)) {
      return true;
    }

    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {
      queryParams: {
        returnUrl: currentUrl.trim(),
      },
    });
  }
};

export const moderationGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const currentUrl = state.url || router.url;

  if (!await authService.ensureActiveSession()) {
    return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {queryParams: {returnUrl: currentUrl}});
  }

  try {
    const currentUser = await firstValueFrom(authService.getCurrentUser());
    return currentUser.roleCode === 'Moderator' || currentUser.roleCode === 'Administrator'
      ? true
      : router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/home')]);
  } catch (error) {
    if (error instanceof AuthError && error.status === 401 && error.code === 'InvalidSession') {
      return false;
    }

    return shouldAllowBackendRecovery(error)
      ? true
      : router.createUrlTree([buildLocalizedRedirectPath(currentUrl, '/login')], {queryParams: {returnUrl: currentUrl}});
  }
};

export const guestGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const targetPathname = isNativeCapacitorPlatform() ? '/home' : '/';
  const currentUrl = state.url || router.url;

  if (!authService.isAuthenticated()) {
    return true;
  }

  if (isNativeGoogleAuthLoginUrl(currentUrl)) {
    const loginPath = buildLocalizedRedirectPath(currentUrl, '/login');
    const logoutPath = buildLocalizedRedirectPath(currentUrl, '/logout');
    return router.createUrlTree([logoutPath], {
      queryParams: {
        next: `${loginPath}?nativeGoogleAuth=1`,
      },
    });
  }

  const qrToken = readQrTokenFromUrl(currentUrl);
  return router.createUrlTree([buildLocalizedRedirectPath(currentUrl, targetPathname)], qrToken
    ? {queryParams: {qr: qrToken}}
    : undefined);
};

function isAdministrator(user: CurrentUserResponse): boolean {
  return user.roleCode === 'Administrator';
}
